/* eslint-disable import/no-cycle */

import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { faCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/sharp-solid-svg-icons';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { AuthContext } from '../../shared/context/auth-context';
import { WhiteBackgroundModal } from '../../shared/modal/whitePopUp/WhiteBackgroundModal';
import { useAddGoalMutation, useDeleteGoalMutation } from '../goalsApiSlice';

function GoalSelector(props) {
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const possibleGoals = useSelector((state: any) => state.goals.possibleGoals);
  const goals = useSelector((state: any) => state.goals.goals);
  const [addGoalAPI] = useAddGoalMutation();
  const [deleteGoalAPI] = useDeleteGoalMutation();
  const [activeIds, setActiveIds] = useState([]);
  const [clickedGoal, setClickedGoal] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const errorMessages = [
    { id: 'tooManyGoals', message: t('G023') },
    { id: 'noGoals', message: t('G053') },
  ];
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    if (goals.length > 0) {
      setActiveIds(goals.map((goal) => goal.id));
    }
    setLoading(false);
  }, [goals]);

  const handleAddGoal = async (newGoal) => {
    try {
      await addGoalAPI({
        token: auth.token,
        goalId: newGoal.id,
      });
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  const handleDeleteGoal = async (oldGoal) => {
    try {
      await deleteGoalAPI({
        token: auth.token,
        goalId: oldGoal.id,
      });
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  const handleClick = (goal) => {
    setLoading(true);
    setPendingId(goal.id);
    setError(null);
    setClickedGoal(goal);
    if (activeIds.includes(goal.id)) {
      if (activeIds.length === 1 && goals.some((item) => item.id === goal.id)) {
        setError(errorMessages[1]);
        setLoading(false);
        setPendingId(null);
      } else if (activeIds.length > 1 && goals.some((item) => item.id === goal.id)) {
        setOpenModal(true);
      }
    } else {
      // selecting a goal that is not active
      if (activeIds.length >= 3) {
        setLoading(false);
        setPendingId(null);
        setError(errorMessages[0]);
      } else {
        handleAddGoal(goal);
      }
    }
  };

  const handleCloseModal = (clickedGoal) => {
    handleDeleteGoal(clickedGoal);
    setOpenModal(false);
  };

  const handleCancelButton = () => {
    setOpenModal(false);
    setLoading(false);
  };

  const saveChanges = () => {
    props.handleClose();
  };

  return (
    <>
      <WhiteBackgroundModal
        hasCloseIcon
        id="goal-selector"
        title={{ text: t('G011'), showTitle: true }}
        submitButton={{
          text: t('M018'),
          icon: faArrowRight,
          onClick: saveChanges,
        }}
        handleClose={props.handleClose}
        dialogTitle={t('modal.goals.info')}
      >
        <fieldset className="btns-container fs-exclude">
          <legend className="visuallyHidden" id="goal-selector-legend">
            {t('G011')}
          </legend>
          {possibleGoals.map((goal) => {
            const isActive = activeIds.includes(goal.id);

            return (
              <div key={goal.id}>
                <button
                  type="button"
                  onClick={() => handleClick(goal)}
                  disabled={loading && pendingId !== goal.id}
                  className={`transparent-button goal-btn  ${
                    clickedGoal?.id === goal.id && error ? 'error' : ''
                  } ${isActive ? 'active' : ''} `}
                  aria-labelledby={`goal-${goal.id}-status`}
                >
                  <span id={`goal-${goal.id}-status`} hidden>
                    {t(goal.title)}
                    {isActive
                      ? t('goal.addGoalsModal.isActive')
                      : t('goal.addGoalsModal.isNotActive')}
                  </span>
                  <p aria-hidden="true">{t(goal.title)}</p>
                  <FontAwesomeIcon
                    icon={isActive ? faCheckCircle : faCircle}
                    className="icon"
                    color="var(--color-secondary-orange)"
                  />
                </button>

                {clickedGoal?.id === goal.id && error && (
                  <div className="goal-error" role="alert">
                    {error.message}
                  </div>
                )}
              </div>
            );
          })}
        </fieldset>
      </WhiteBackgroundModal>
      {error && (
        <div role="alert" aria-live="assertive" className="sr-only">
          {error.message}
        </div>
      )}
      {openModal && (
        <WhiteBackgroundModal
          hasCloseIcon
          id="goal-remover"
          title={{ text: t('G044'), showTitle: true }}
          size="small"
          submitButton={{
            text: t('R013'),
            icon: faArrowRight,
            onClick: () => handleCloseModal(clickedGoal),
          }}
          cancelButton={{
            text: t('G055'),
            onClick: handleCancelButton,
          }}
          handleClose={props.handleClose}
          dialogTitle={t('modal.goals.remove')}
        >
          <div className="goal-remover-content">
            <h3>{t('S020')}</h3>
            <p>{t('G054')}</p>
          </div>
        </WhiteBackgroundModal>
      )}
    </>
  );
}

export default GoalSelector;
